/*  ========================================================================
    # Scripts - Contact Persons Slider
    ========================================================================  */

/**
 * Script that handles the contact persons slider functionality.
 */
const ContactPersonsSlider = {
  cfg: {
    //
  },

  /**
   * Initialize a single slider instance.
   */
  initSlider(slider) {
    const sliderInstance = {
      slider,
      track: slider.querySelector('.s-contact-persons__slider-axis'),
      slides: slider.querySelectorAll('.s-contact-persons__slide'),
      prevBtn: slider.querySelector('.s-contact-persons__navigation-button--prev'),
      nextBtn: slider.querySelector('.s-contact-persons__navigation-button--next'),
      progressTrack: slider.querySelector('.s-contact-persons__progress-track'),
      progressThumb: slider.querySelector('.s-contact-persons__progress-thumb'),
      totalSlides: parseInt(slider.dataset.totalSlides, 10),
      slidesPerView: this.getSlidesPerView(),
      currentSlide: 0,
      maxSlides: 0,
      isTrackDragging: false,
      trackStartX: 0,
      dragOffset: 0,
      resizeTimeout: null
    };

    sliderInstance.maxSlides = Math.max(0, sliderInstance.totalSlides - sliderInstance.slidesPerView);

    this.updateSlider(sliderInstance);
    this.bindEvents(sliderInstance);
    this.handleResize(sliderInstance);
  },

  /**
   * Get the number of slides per view based on screen size.
   */
  getSlidesPerView() {
    const width = window.innerWidth;
    if (width >= 1280) return 3; // lg breakpoint
    if (width >= 768) return 2; // md breakpoint
    return 1; // sm breakpoint
  },

  /**
   * Bind events for a slider instance.
   */
  bindEvents(sliderInstance) {
    // Navigation buttons
    sliderInstance.prevBtn?.addEventListener('click', () => this.prevSlide(sliderInstance));
    sliderInstance.nextBtn?.addEventListener('click', () => this.nextSlide(sliderInstance));

    // Progress track events
    sliderInstance.progressTrack?.addEventListener('click', e => this.handleTrackClick(e, sliderInstance));
    sliderInstance.progressThumb?.addEventListener('mousedown', e => this.handleTrackDragStart(e, sliderInstance));

    // Store reference to slider instance for drag events
    sliderInstance.handleTrackDragMove = e => this.handleTrackDragMove(e, sliderInstance);
    sliderInstance.handleTrackDragEnd = () => this.handleTrackDragEnd(sliderInstance);

    document.addEventListener('mousemove', sliderInstance.handleTrackDragMove);
    document.addEventListener('mouseup', sliderInstance.handleTrackDragEnd);

    // Window resize
    window.addEventListener('resize', () => this.handleResize(sliderInstance));
  },

  /**
   * Go to previous slide.
   */
  prevSlide(sliderInstance) {
    if (sliderInstance.currentSlide > 0) {
      sliderInstance.currentSlide--;
      this.updateSlider(sliderInstance);
    }
  },

  /**
   * Go to next slide.
   */
  nextSlide(sliderInstance) {
    if (sliderInstance.currentSlide < sliderInstance.maxSlides) {
      sliderInstance.currentSlide++;
      this.updateSlider(sliderInstance);
    }
  },

  /**
   * Go to specific slide.
   */
  goToSlide(index, sliderInstance) {
    sliderInstance.currentSlide = Math.min(Math.max(0, index), sliderInstance.maxSlides);
    this.updateSlider(sliderInstance);
  },

  /**
   * Handle track click events.
   */
  handleTrackClick(event, sliderInstance) {
    if (sliderInstance.isTrackDragging) return;

    // Don't handle clicks on the thumb itself
    if (e.target === sliderInstance.progressThumb) return;

    const rect = sliderInstance.progressTrack.getBoundingClientRect();
    const clickX = event.clientX - rect.left;
    const thumbWidth = sliderInstance.progressThumb.offsetWidth;
    const availableWidth = rect.width - thumbWidth;
    const percentage = Math.max(0, Math.min(1, (clickX - thumbWidth / 2) / availableWidth));
    const targetSlide = Math.round(percentage * sliderInstance.maxSlides);

    this.goToSlide(targetSlide, sliderInstance);
  },

  /**
   * Handle track drag start.
   */
  handleTrackDragStart(event, sliderInstance) {
    event.preventDefault();
    sliderInstance.isTrackDragging = true;
    const rect = sliderInstance.progressThumb.getBoundingClientRect();
    sliderInstance.dragOffset = event.clientX - rect.left;
    sliderInstance.progressThumb.classList.add('is-dragging');
    document.body.style.userSelect = 'none';
  },

  /**
   * Handle track drag move.
   */
  handleTrackDragMove(event, sliderInstance) {
    if (!sliderInstance || !sliderInstance.isTrackDragging) return;

    const trackRect = sliderInstance.progressTrack.getBoundingClientRect();
    const thumbWidth = sliderInstance.progressThumb.offsetWidth;
    const availableWidth = trackRect.width - thumbWidth;
    const newLeft = event.clientX - trackRect.left - sliderInstance.dragOffset;
    const percentage = Math.max(0, Math.min(1, newLeft / availableWidth));
    const targetSlide = Math.round(percentage * sliderInstance.maxSlides);

    this.goToSlide(targetSlide, sliderInstance);
  },

  /**
   * Handle track drag end.
   */
  handleTrackDragEnd(sliderInstance) {
    if (!sliderInstance || !sliderInstance.isTrackDragging) return;

    sliderInstance.isTrackDragging = false;
    sliderInstance.progressThumb?.classList.remove('is-dragging');
    document.body.style.userSelect = '';
  },

  /**
   * Handle window resize.
   */
  handleResize(sliderInstance) {
    clearTimeout(sliderInstance.resizeTimeout);
    sliderInstance.resizeTimeout = setTimeout(() => {
      const newSlidesPerView = this.getSlidesPerView();
      if (newSlidesPerView !== sliderInstance.slidesPerView) {
        sliderInstance.slidesPerView = newSlidesPerView;
        sliderInstance.maxSlides = Math.max(0, sliderInstance.totalSlides - sliderInstance.slidesPerView);
        sliderInstance.currentSlide = Math.min(sliderInstance.currentSlide, sliderInstance.maxSlides);
        this.updateSlider(sliderInstance);
      }
    }, 250);
  },

  /**
   * Update slider position and controls.
   */
  updateSlider(sliderInstance) {
    // Update transform
    const translateX = -(sliderInstance.currentSlide * (100 / sliderInstance.slidesPerView));
    sliderInstance.track.style.transform = `translateX(${translateX}%)`;

    // Show/hide navigation based on whether scrolling is needed
    const navigationNeeded = sliderInstance.maxSlides > 0;
    sliderInstance.prevBtn.style.display = navigationNeeded ? '' : 'none';
    sliderInstance.nextBtn.style.display = navigationNeeded ? '' : 'none';
    sliderInstance.progressTrack.style.display = navigationNeeded ? '' : 'none';

    // Update navigation buttons
    sliderInstance.prevBtn.disabled = sliderInstance.currentSlide === 0;
    sliderInstance.nextBtn.disabled = sliderInstance.currentSlide >= sliderInstance.maxSlides;

    // Update scrollbar thumb
    if (sliderInstance.maxSlides > 0) {
      // Calculate thumb size based on visible slides vs total slides
      const thumbSizePercent = (sliderInstance.slidesPerView / sliderInstance.totalSlides) * 100;
      // Calculate thumb position
      const thumbPositionPercent = (sliderInstance.currentSlide / sliderInstance.maxSlides) * (100 - thumbSizePercent);

      sliderInstance.progressThumb.style.width = `${Math.max(20, thumbSizePercent)}%`;
      sliderInstance.progressThumb.style.left = `${thumbPositionPercent}%`;
    } else {
      // If no scrolling needed, thumb fills entire track
      sliderInstance.progressThumb.style.width = '100%';
      sliderInstance.progressThumb.style.left = '0%';
    }
  },

  /**
   * Initialize all sliders.
   */
  init() {
    document.querySelectorAll('.s-contact-persons__slider').forEach(slider => {
      this.initSlider(slider);
    });
  }
};

export default ContactPersonsSlider;
